import { useState } from "react";
import { Download, Send, Check, X, Upload, Trash2, ShieldCheck, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Panel } from "@/components/studio/common";
import { api, downloadFile, dt, formatErr } from "@/lib/api";

const M_STATUS = { generato: "Generato, in attesa di firma", caricato: "Firmato - da validare", validato: "Validato", respinto: "Respinto" };
const DOC_TIPI = [["documento_identita", "Documento d'identità"], ["codice_fiscale", "Codice fiscale"], ["atto_successione", "Atto di successione"],
  ["certificato_morte", "Certificato di morte"], ["dichiarazione_sostitutiva", "Dichiarazione sostitutiva"], ["mandato_firmato", "Mandato firmato"],
  ["istanza_consap", "Istanza Consap"], ["ricevuta_bonifico", "Ricevuta bonifico"], ["nota_compenso", "Nota di compenso"], ["altro", "Altro"]];
const TIPO_LABEL = Object.fromEntries(DOC_TIPI);

const SigCheck = ({ sc }) => {
  const ok = sc.intact && sc.valid;
  const Icon = ok ? ShieldCheck : ShieldAlert;
  return (
    <div data-testid="mandato-signature-check" className={`rounded-lg border p-3 text-sm mt-3 ${ok ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
      <p className="flex items-center gap-2 font-semibold"><Icon className="h-4 w-4" />{sc.format}: {sc.message}</p>
      {sc.signer && <p className="text-xs mt-1 break-all">Firmatario: {sc.signer}</p>}
      {sc.issuer && <p className="text-xs break-all">Emittente: {sc.issuer}</p>}
      <p className="text-xs mt-1 text-slate-600">Catena di fiducia EU: {sc.trusted ? "verificata" : "non verificata automaticamente — controllare il certificatore"}</p>
    </div>
  );
};

export const MandatoCard = ({ p, setP, user }) => {
  const m = p.mandato;
  const [note, setNote] = useState("");
  const senior = user.role !== "segretaria";
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); } catch (e) { toast.error(formatErr(e)); } };
  const sendLink = () => act(() => api.post(`/pratiche/${p.id}/send-mandate-link`).then(() => api.get(`/pratiche/${p.id}`)).then((r) => setP(r.data)), "Link mandato inviato al cliente");
  const validate = (approve) => act(() => api.post(`/pratiche/${p.id}/mandato/validate`, { approve, note }).then((r) => setP(r.data)), approve ? "Mandato validato" : "Mandato respinto");

  return (
    <Panel title="Mandato" testid="pratica-mandato-card"
      action={<Button size="sm" variant="outline" data-testid="send-mandate-link-btn" onClick={sendLink}><Send className="h-3.5 w-3.5 mr-1.5" />Invia link al cliente</Button>}>
      {!m ? <p className="text-sm text-slate-500">Il cliente non ha ancora generato il mandato.</p> : (
        <>
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span className="font-semibold text-navy" data-testid="mandato-status">{M_STATUS[m.status]}</span>
            {m.sign_mode && <span className="text-slate-500">· {m.sign_mode === "digitale" ? "Firma digitale" : "Firma analogica"} · {dt(m.signed_at)}</span>}
          </div>
          {m.signature_check && <SigCheck sc={m.signature_check} />}
          {m.sign_mode === "analogico" && <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 mt-3">Firma analogica scansionata: valore probatorio limitato. Valutare la richiesta dell'originale cartaceo.</p>}
          <div className="flex flex-wrap gap-2 mt-4">
            <Button size="sm" variant="outline" data-testid="mandato-download-pdf" onClick={() => downloadFile(`/pratiche/${p.id}/mandato/pdf`, "mandato.pdf")}><Download className="h-3.5 w-3.5 mr-1.5" />PDF generato</Button>
            {m.signed_path && <Button size="sm" variant="outline" data-testid="mandato-download-signed" onClick={() => downloadFile(`/pratiche/${p.id}/mandato/signed`, m.signed_filename)}><Download className="h-3.5 w-3.5 mr-1.5" />Mandato firmato</Button>}
          </div>
          {m.status === "caricato" && senior && (
            <div className="mt-4 border-t border-slate-100 pt-4 space-y-2">
              <Textarea data-testid="mandato-validate-note" placeholder="Nota di validazione (facoltativa)" value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
              <div className="flex gap-2">
                <Button size="sm" data-testid="mandato-approve-btn" className="bg-emerald-700 hover:bg-emerald-800" onClick={() => validate(true)}><Check className="h-3.5 w-3.5 mr-1.5" />Valida mandato</Button>
                <Button size="sm" variant="outline" data-testid="mandato-reject-btn" className="text-red-700" onClick={() => validate(false)}><X className="h-3.5 w-3.5 mr-1.5" />Respingi</Button>
              </div>
            </div>
          )}
        </>
      )}
    </Panel>
  );
};

export const DocumentiCard = ({ p, load, user }) => {
  const [tipo, setTipo] = useState("documento_identita");
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("tipo", tipo);
    fd.append("file", file);
    try { await api.post(`/pratiche/${p.id}/documents`, fd); toast.success("Documento caricato"); load(); }
    catch (ex) { toast.error(formatErr(ex)); } finally { setBusy(false); e.target.value = ""; }
  };
  const remove = async (id) => { try { await api.delete(`/pratiche/${p.id}/documents/${id}`); load(); } catch (ex) { toast.error(formatErr(ex)); } };
  const isErede = p.mandato?.dati?.qualita === "erede";

  return (
    <Panel title="Documenti" testid="pratica-documenti-card">
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <Select value={tipo} onValueChange={setTipo}>
          <SelectTrigger data-testid="doc-tipo-select" className="sm:w-64"><SelectValue /></SelectTrigger>
          <SelectContent>{DOC_TIPI.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <label className={`inline-flex items-center justify-center gap-2 px-4 h-9 rounded-md text-sm text-white bg-navy hover:bg-navy-800 cursor-pointer ${busy ? "opacity-60" : ""}`}>
          <Upload className="h-4 w-4" />{busy ? "Caricamento…" : "Carica file"}
          <input data-testid="doc-upload-input" type="file" className="hidden" onChange={upload} disabled={busy} />
        </label>
      </div>
      <p className="text-xs text-slate-500 mb-3">Checklist: documento d'identità, codice fiscale{isErede ? ", atto di successione / dichiarazione sostitutiva, certificato di morte" : ""}.</p>
      <ul className="divide-y divide-slate-100">
        {p.documenti.map((doc) => (
          <li key={doc.id} className="py-2.5 flex items-center gap-3 text-sm" data-testid={`doc-row-${doc.id}`}>
            <span className="text-[11px] font-semibold uppercase tracking-wider text-gold-dark w-40 shrink-0">{TIPO_LABEL[doc.tipo]}</span>
            <span className="flex-1 truncate">{doc.filename}</span>
            <span className="text-xs text-slate-400 hidden sm:inline">{doc.uploaded_by} · {dt(doc.created_at)}</span>
            <button data-testid={`doc-download-${doc.id}`} onClick={() => downloadFile(`/pratiche/${p.id}/documents/${doc.id}/download`, doc.filename)} className="text-navy hover:text-gold-dark"><Download className="h-4 w-4" /></button>
            {user.role !== "segretaria" && <button data-testid={`doc-delete-${doc.id}`} onClick={() => remove(doc.id)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>}
          </li>
        ))}
        {!p.documenti.length && <li className="text-sm text-slate-500 py-2">Nessun documento caricato.</li>}
      </ul>
    </Panel>
  );
};
